import React, { useContext, useEffect, useState, useMemo } from "react";

// Context
import { CoreContext } from "../../context/global/CoreContext";

// Shared ticket components
import {
  TicketLogTable,
  TicketJobSection,
  TicketDescriptionSection,
  TicketCommentsSection,
  TicketStatusSection,
  TicketFormContainer,
  TicketFormSection,
  TicketFormActions,
} from "../../features/tickets";

// UI Components
import { Button, WorkerInitials } from "../../components/ui";
import { Pencil, Eye, Check, ChevronDown, ChevronUp } from "../../components/icons/svg/outline";
import { formatDate, formatDateTime } from "../../utils/datetime";

/**
 * TicketDemo - Demonstration page for the shared ticket system
 * 
 * This page provides a working example of the shared ticket components
 * for developers building new ticket-based modules (like T&M, RFI, etc.)
 * 
 * Demonstrates:
 * - TicketLogTable with configurable columns
 * - Custom RowComponent for module-specific rendering
 * - Status filtering and search functionality
 * - Mock data structure matching the BaseTicket model
 */

// ============================================================================
// MOCK DATA - Simulates data from BaseTicket-based API
// ============================================================================

const MOCK_JOBS = [
  { id: 1, number: "2024-001", description: "Highway Expansion Project" },
  { id: 2, number: "2024-002", description: "Bridge Renovation" },
  { id: 3, number: "2024-003", description: "Commercial Plaza Development" },
];

const MOCK_USERS = [
  { id: 1, first: "John", last: "Smith" },
  { id: 2, first: "Sarah", last: "Johnson" },
  { id: 3, first: "Mike", last: "Williams" },
];

/**
 * Generate mock ticket data
 * Structure mirrors what BaseTicket provides plus module-specific fields
 */
const generateMockTickets = () => {
  const statuses = ["draft", "submitted", "approved", "rejected"];
  const priorities = ["low", "medium", "high"];
  
  return Array.from({ length: 15 }, (_, i) => {
    const job = MOCK_JOBS[i % MOCK_JOBS.length];
    const user = MOCK_USERS[i % MOCK_USERS.length];
    const status = statuses[i % statuses.length];
    const daysAgo = Math.floor(Math.random() * 30);
    
    return {
      id: i + 1,
      // BaseTicket fields
      ticket_id: `DEMO-${job.number}-${String(i + 1).padStart(3, "0")}`,
      job: job.id,
      job_number: job.number,
      job_description: job.description,
      date_performed: new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      description: [
        "Unexpected rock formation encountered during excavation",
        "Weather delay - heavy rainfall caused site flooding",
        "Equipment malfunction - crane hydraulic failure",
        "Material shortage - awaiting delivery confirmation",
        "Safety inspection required before proceeding",
      ][i % 5],
      comments: i % 3 === 0 ? "Additional notes from the field team" : null,
      created_by: user,
      created_at: new Date(Date.now() - (daysAgo + 2) * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString(),
      
      // Module-specific fields (example)
      status: status,
      priority: priorities[i % priorities.length],
      submitted: status !== "draft",
      submitted_at: status !== "draft" 
        ? new Date(Date.now() - (daysAgo - 1) * 24 * 60 * 60 * 1000).toISOString() 
        : null,
    };
  });
};

// ============================================================================
// STATUS INDICATOR COMPONENT - Module-specific status display
// ============================================================================

/**
 * DemoStatusIndicator - Example of a module-specific status component
 * Each module (T&M, RFI, etc.) would have its own version
 */
const DemoStatusIndicator = ({ item }) => {
  const getStatusStyle = () => {
    switch (item.status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "submitted":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityDot = () => {
    switch (item.priority) {
      case "high":
        return "bg-red-500";
      case "medium":
        return "bg-yellow-500";
      default:
        return "bg-gray-300";
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${getPriorityDot()}`} />
      <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusStyle()}`}>
        {item.status}
      </span>
    </div>
  );
};

// ============================================================================
// CUSTOM CARD COMPONENT - Module-specific card rendering
// ============================================================================

/**
 * DemoTicketCard - Example custom card component
 * 
 * Shows how to create a module-specific card that matches the row component
 * but displays in a card format for the grid view.
 */
const DemoTicketCard = ({ item, onView, onEdit, onApprove }) => {
  const projectDisplay = item.job_number && item.job_description
    ? `${item.job_number} - ${item.job_description}`
    : "N/A";

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <span className="font-mono text-sm font-semibold text-blue-600">
          {item.ticket_id}
        </span>
        <DemoStatusIndicator item={item} />
      </div>

      {/* Description */}
      <p className="text-sm text-gray-700 line-clamp-2 mb-3">
        {item.description || "No description"}
      </p>

      {/* Details */}
      <div className="space-y-1 text-xs text-gray-500 mb-4">
        <div className="flex justify-between">
          <span className="font-medium">Project:</span>
          <span className="text-gray-700 truncate ml-2 max-w-[60%]">{projectDisplay}</span>
        </div>
        <div className="flex justify-between">
          <span className="font-medium">Date:</span>
          <span className="text-gray-700">
            {item.date_performed ? formatDate(item.date_performed, "mon DD, YYYY") : "N/A"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="font-medium">Updated:</span>
          <span className="text-gray-700">
            {item.updated_at ? formatDateTime(item.updated_at, "mon DD, YYYY") : "N/A"}
          </span>
        </div>
        {item.created_by && (
          <div className="flex justify-between items-center">
            <span className="font-medium">Created by:</span>
            <span className="text-gray-700">{item.created_by.first} {item.created_by.last}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button
          variant="custom"
          text="View"
          icon={<Eye className="w-4 h-4 text-blue-700" />}
          onClick={() => onView?.(item)}
          className="text-xs border-[1px] text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-200 px-2 py-1.5"
        />
        <Button
          variant="custom"
          text="Edit"
          icon={<Pencil className="w-4 h-4 text-green-700" />}
          onClick={() => onEdit?.(item)}
          className="text-xs border-[1px] text-green-700 border-green-300 bg-green-50 hover:bg-green-200 px-2 py-1.5"
        />
        {item.status === "submitted" && (
          <Button
            variant="custom"
            text="Approve"
            icon={<Check className="w-4 h-4 text-purple-700" />}
            onClick={() => onApprove?.(item)}
            className="text-xs border-[1px] text-purple-700 border-purple-300 bg-purple-50 hover:bg-purple-200 px-2 py-1.5"
          />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// CUSTOM ROW COMPONENT - Module-specific row rendering
// ============================================================================

/**
 * DemoTicketRow - Example custom row component
 * 
 * Shows how to create a module-specific row that:
 * - Renders data according to column config
 * - Includes custom status indicators
 * - Has module-specific action buttons
 */
const DemoTicketRow = ({ item, columns, onView, onEdit, onApprove }) => {
  // Project display
  const projectDisplay = item.job_number && item.job_description
    ? `${item.job_number} - ${item.job_description}`
    : "N/A";

  return (
    <tr className="hover:bg-gray-50 text-sm">
      {/* Ticket ID - clickable */}
      <td className="px-2 md:px-6 py-4 whitespace-nowrap font-mono text-[15px] cursor-pointer hover:underline text-blue-600">
        {item.ticket_id}
      </td>
      
      {/* Status - custom component */}
      <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
        <DemoStatusIndicator item={item} />
      </td>
      
      {/* Project */}
      <td className="hidden md:table-cell px-6 py-4 max-w-[160px]">
        <div className="truncate">{projectDisplay}</div>
      </td>
      
      {/* Date Performed */}
      <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
        {item.date_performed
          ? formatDate(item.date_performed, "d mon DD, YYYY")
          : "N/A"}
      </td>
      
      {/* Last Updated */}
      <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-gray-500">
        {item.updated_at
          ? formatDateTime(item.updated_at, "mon DD, YYYY")
          : "N/A"}
      </td>
      
      {/* Created By */}
      <td className="hidden md:table-cell px-2 py-4 max-w-[40px]">
        {item.created_by && (
          <WorkerInitials worker={item.created_by} className="bg-gray-200" tooltip />
        )}
      </td>
      
      {/* Description */}
      <td className="px-2 md:px-6 py-4 max-w-md">
        <div className="text-xs line-clamp-2">
          {item.description || "No description"}
        </div>
      </td>
      
      {/* Actions */}
      <td className="px-2 md:p-4 py-3 whitespace-nowrap">
        <div className="flex gap-2">
          <Button
            variant="custom"
            text="View"
            icon={<Eye className="w-4 h-4 text-blue-700 translate-x-[2px]" />}
            onClick={() => onView?.(item)}
            className="text-sm border-[1px] text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-200 ease-in-out px-1 pr-2 py-1"
          />
          <Button
            variant="custom"
            text="Edit"
            icon={<Pencil className="w-4 h-4 text-green-700 translate-x-[2px]" />}
            onClick={() => onEdit?.(item)}
            className="hidden md:inline-flex text-sm border-[1px] text-green-700 border-green-300 bg-green-50 hover:bg-green-200 ease-in-out px-1 pr-2 py-1"
          />
          {item.status === "submitted" && (
            <Button
              variant="custom"
              text="Approve"
              icon={<Check className="w-4 h-4 text-purple-700 translate-x-[2px]" />}
              onClick={() => onApprove?.(item)}
              className="hidden md:inline-flex text-sm border-[1px] text-purple-700 border-purple-300 bg-purple-50 hover:bg-purple-200 ease-in-out px-1 pr-2 py-1"
            />
          )}
        </div>
      </td>
    </tr>
  );
};

// ============================================================================
// MAIN DEMO PAGE COMPONENT
// ============================================================================

export default function TicketDemo() {
  const { appName } = useContext(CoreContext);
  const [tickets] = useState(generateMockTickets);
  const [filteredCount, setFilteredCount] = useState(0);
  const [showFormDemo, setShowFormDemo] = useState(false);
  
  // Form demo state
  const [formData, setFormData] = useState({
    job: null,
    date_performed: "",
    description: "",
    comments: "",
    submitted: false,
    locked: false,
  });

  // Set page title
  useEffect(() => {
    document.title = "Ticket System Demo";
    appName.current = "Ticket System Demo";
  }, [appName]);

  // ========================================================================
  // COLUMN CONFIGURATION
  // Define how columns are displayed, sorted, and filtered
  // ========================================================================
  
  const columns = useMemo(() => [
    {
      key: "ticket_id",
      label: "Ticket ID",
      sortable: true,
      headerClassName: "px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
    },
    {
      key: "status",
      label: "Status",
      sortable: false,
      hideOnMobile: true,
    },
    {
      key: "project",
      label: "Project",
      sortable: true,
      hideOnMobile: true,
      // Custom sort value getter for computed fields
      getSortValue: (item) => 
        item.job_number ? `${item.job_number} - ${item.job_description}` : "",
    },
    {
      key: "date_performed",
      label: "Date Performed",
      sortable: true,
      hideOnMobile: true,
    },
    {
      key: "updated_at",
      label: "Last Updated",
      sortable: true,
      hideOnMobile: true,
    },
    {
      key: "created_by",
      label: "By",
      sortable: false,
      hideOnMobile: true,
      headerClassName: "hidden md:table-cell max-w-[40px] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      headerClassName: "px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      headerClassName: "px-2 md:p-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
    },
  ], []);

  // ========================================================================
  // STATUS FILTER CONFIGURATION
  // Define available status filters and filter logic
  // ========================================================================

  const statusFilters = [
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const statusFilterFn = (item, selectedStatus) => {
    return item.status === selectedStatus;
  };

  // ========================================================================
  // ACTION HANDLERS
  // ========================================================================

  const handleView = (item) => {
    alert(`Viewing ticket: ${item.ticket_id}\n\nIn a real module, this would navigate to the detail view.`);
  };

  const handleEdit = (item) => {
    alert(`Editing ticket: ${item.ticket_id}\n\nIn a real module, this would navigate to the edit form.`);
  };

  const handleApprove = (item) => {
    alert(`Approving ticket: ${item.ticket_id}\n\nIn a real module, this would call the API to approve.`);
  };

  return (
    <div className="min-h-screen font-inter caret-transparent bg-custom-rsbg">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Ticket System Demo</h1>
              <p className="text-sm text-gray-500">
                Working example of the shared ticket components for developers
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Showing</div>
              <div className="text-2xl font-bold text-blue-600">{filteredCount}</div>
              <div className="text-sm text-gray-500">tickets</div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h2 className="font-semibold text-blue-800 mb-2">Developer Reference</h2>
          <p className="text-sm text-blue-700 mb-2">
            This page demonstrates the shared ticket system components. Use this as a reference when building new ticket-based modules.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <strong className="text-blue-800">Key Files:</strong>
              <ul className="list-disc list-inside text-blue-600 mt-1">
                <li><code className="text-xs">features/tickets/</code></li>
                <li><code className="text-xs">components/TicketLogTable/</code></li>
                <li><code className="text-xs">context/createTicketContext.js</code></li>
              </ul>
            </div>
            <div>
              <strong className="text-blue-800">Components:</strong>
              <ul className="list-disc list-inside text-blue-600 mt-1">
                <li>TicketLogTable (main table)</li>
                <li>TicketCardView (card grid)</li>
                <li>TicketLogHeader / TicketCard</li>
              </ul>
            </div>
            <div>
              <strong className="text-blue-800">Features:</strong>
              <ul className="list-disc list-inside text-blue-600 mt-1">
                <li>List/Card view toggle</li>
                <li>Search & filtering</li>
                <li>Custom row/card components</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <TicketLogTable
            data={tickets}
            columns={columns}
            RowComponent={DemoTicketRow}
            CardComponent={DemoTicketCard}
            searchFields={["ticket_id", "description", "job_number"]}
            searchPlaceholder="Search by ID, description, or job number..."
            statusFilters={statusFilters}
            statusFilterFn={statusFilterFn}
            emptyMessage="No demo tickets found"
            onFilteredCountChange={setFilteredCount}
            defaultSort={{ field: "updated_at", direction: "desc" }}
            rowProps={{
              onView: handleView,
              onEdit: handleEdit,
              onApprove: handleApprove,
            }}
            cardProps={{
              onView: handleView,
              onEdit: handleEdit,
              onApprove: handleApprove,
            }}
          />
        </div>
      </div>

      {/* Form Sections Demo */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <button
          onClick={() => setShowFormDemo(!showFormDemo)}
          className="w-full bg-white rounded-lg shadow p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Pencil className="w-5 h-5 text-green-700" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-gray-900">Form Section Components</h2>
              <p className="text-sm text-gray-500">Click to {showFormDemo ? "hide" : "view"} the shared form components demo</p>
            </div>
          </div>
          {showFormDemo 
            ? <ChevronUp className="w-6 h-6 text-gray-400" />
            : <ChevronDown className="w-6 h-6 text-gray-400" />
          }
        </button>

        {showFormDemo && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-green-800 mb-2">Form Components Reference</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <strong className="text-green-800">Components:</strong>
                <ul className="list-disc list-inside text-green-600 mt-1">
                  <li><code className="text-xs">TicketJobSection</code> - Job & date selection</li>
                  <li><code className="text-xs">TicketDescriptionSection</code> - Description textarea</li>
                  <li><code className="text-xs">TicketCommentsSection</code> - Comments field</li>
                </ul>
              </div>
              <div>
                <strong className="text-green-800">Layout Components:</strong>
                <ul className="list-disc list-inside text-green-600 mt-1">
                  <li><code className="text-xs">TicketFormContainer</code> - Form wrapper</li>
                  <li><code className="text-xs">TicketFormSection</code> - Section grouping</li>
                  <li><code className="text-xs">TicketFormActions</code> - Action buttons</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {showFormDemo && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <TicketFormContainer
              onSubmit={() => alert(`Form submitted!\n\nData: ${JSON.stringify(formData, null, 2)}`)}
              className="shadow-none border-0"
            >
              {/* Status Section */}
              <TicketFormSection>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-700">Demo Ticket Form</h2>
                  <TicketStatusSection
                    data={formData}
                    onToggleSubmit={() => setFormData(prev => ({ ...prev, submitted: !prev.submitted }))}
                    onToggleLock={() => setFormData(prev => ({ ...prev, locked: !prev.locked }))}
                  />
                </div>
              </TicketFormSection>

              {/* Job Information */}
              <TicketFormSection>
                <TicketJobSection
                  data={formData}
                  onChange={setFormData}
                  readOnly={formData.locked}
                />
              </TicketFormSection>

              {/* Description */}
              <TicketFormSection>
                <TicketDescriptionSection
                  data={formData}
                  onChange={setFormData}
                  readOnly={formData.locked}
                  placeholder="Describe the issue, request, or work performed..."
                  maxLength={500}
                />
              </TicketFormSection>

              {/* Comments */}
              <TicketFormSection>
                <TicketCommentsSection
                  data={formData}
                  onChange={setFormData}
                  readOnly={formData.locked}
                  showBorder={false}
                />
              </TicketFormSection>

              {/* Actions */}
              <TicketFormActions>
                <Button
                  type="button"
                  variant="custom"
                  text="Cancel"
                  onClick={() => setFormData({ job: null, date_performed: "", description: "", comments: "", submitted: false, locked: false })}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300"
                />
                <Button
                  type="submit"
                  variant="custom"
                  text="Save Ticket"
                  className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700"
                  disabled={formData.locked}
                />
              </TicketFormActions>
            </TicketFormContainer>
          </div>
        )}
      </div>
    </div>
  );
}
